import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { CommonModule } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatCard } from '@angular/material/card';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { v4 as uuid } from 'uuid';
import { BehaviorSubject, filter, take } from 'rxjs';
import { GuestService } from '../services/guest.service';
import { GuestVerificationInfo } from '../models/verification-info/GuestVerificationInfo';
import { Logger } from '../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { UserRegion } from '../../../infrastructure/context/UserContext';
import { DataTableFilterMessageService, ActiveEventMessage } from '../../../shared/components/data-table/filters/components/data-table-filter-message-service';
import { DataTableFilterPillComponent, DataTableFilterPillState } from '../../../shared/components/data-table/filters/components/data-table-filter-pill/data-table-filter-pill.component';
import { DataTableFilter } from '../../../shared/components/data-table/filters/components/DataTableFilter';
import { SearchService } from '../../../shared/search/search.service';
import { SortParams, SortOrderType } from '../../../shared/search/SearchParams';
import { GuestFilterStatusComponent } from '../guests-table/components/guest-filter-status/guest-filter-status.component';
import { GuestVerificationResultBuilder } from '../shared/guest-verification-result-builder';
import { GuestFilterStateService } from './services/guest-filter-state.service';
import { GuestMessageService } from './services/guest-message.service';
import { DataTableFilterComponent } from '../../../shared/components/data-table/filters/components/data-table-filter/data-table-filter.component';

@Component({
  selector: 'app-guest-equifax-lookup',
  templateUrl: './guest-equifax-lookup.component.html',
  styleUrls: ['./guest-equifax-lookup.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinner,
    MatCard,
    MatChip,
    MatIcon,
    DataTableFilterComponent,
    MatFormFieldModule,
    DataTableFilterPillComponent,
    MatTableModule,
    MatChipSet,
    MatTooltip,
    MatPaginator,
    FormsModule
  ]
})
export class GuestEquifaxLookupComponent implements OnInit, AfterViewInit {

  VERIFICATION_RESULT_CODE_PASSED = 1;
  VERIFICATION_RESULT_CODE_CONSIDER = 2;
  VERIFICATION_RESULT_CODE_PROCESSING = 3;
  VERIFICATION_RESULT_CODE_NO_VERIFICATION = 4;

  filterDefinitions: DataTableFilter[] = [];

  tableColumns: string[] = [];

  @Input() displayLoadingIndicator!: BehaviorSubject<boolean>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort();

  dataSource = new MatTableDataSource<GuestVerificationInfo>();
  filterPills = new Map<string, DataTableFilterPillState>();

  loading = new BehaviorSubject<boolean>(false);
  initializing = true;
  pageSize = 10;
  tableLength = 0;
  keyword!: string;
  pageNumber: number = 1;
  legendVisible = true;
  returnEmptyDataSource: boolean = false;

  constructor(
    public userContextProvider: UserContextProvider,
    private router: Router,
    private guestService: GuestService,
    private snackbar: MatSnackBar,
    private searchService: SearchService,
    private guestMessageService: GuestMessageService,
    private guestFilterStateService: GuestFilterStateService,
    private filterMessageService: DataTableFilterMessageService,
    private guestVerificationResultBuilder: GuestVerificationResultBuilder,
    private logService: Logger
  ) { }

  ngOnInit(): void {
    this.userContextProvider.isContextLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(_ => {
      this.resetSearchQuery();
      this.updateFilterPillTextAfterFilterEvent();
      this.setupAfterContextLoaded();
    });
  }

  setupAfterContextLoaded() {
    this.setupTableFilters();
    this.setupTableColumns();
    this.setupFilterEventHandler();
    this.loadPage();
    this.initializing = false;
  }

  setupFilterEventHandler() {
    this.guestMessageService.applyFilterEvent.subscribe(_ => {
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.passed, '1001');
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.consider, '3001');
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.processing, '4001');
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.noVerification, '5001');

      this.loadPage();
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  async loadPage() {
    if (this.returnEmptyDataSource) {
      this.dataSource.data = [];
    } else {
      try {
        this.loading.next(true);
        this.dataSource.data = [];

        const { guests, totalRecords } = await this.guestService.searchGuests(this.searchService.currentQuery);
        const mappedGuests = guests.map(g => this.guestVerificationResultBuilder.buildGuestsFromEquifaxResults(g, this.userContextProvider.region));
        this.updateTableDataSource(mappedGuests, totalRecords);
      } catch (e) {
        this.logService.handleError(e, 'An error occurred while retrieving Guest equifax results', 'An error occurred while retrieving Guest equifax results');
      } finally {
        this.loading.next(false);
      }
    }
  }

  resetSearchQuery() {
    this.searchService.resetQuery();
    this.searchService.setSort('guestId', -1);
  }

  setupTableFilters() {
    const screeningResultFilterName = this.userContextProvider.isNaUser() ? 
      'Screening Result' : 
      'Verification Result';

    this.filterDefinitions = [
      { id: uuid(), name: screeningResultFilterName, shortName: screeningResultFilterName, component: GuestFilterStatusComponent }
    ];
  }

  setupTableColumns() {
    switch(this.userContextProvider.region) {
      case UserRegion.NA:
        this.setupNaTableColumns();
        break;
      case UserRegion.EU:
        this.setupEuTableColumns();
        break;
      default:
        this.logService.handleError(`Unknown region ${this.userContextProvider.region}`, `Unknown region ${this.userContextProvider.region}`, `Unknown region ${this.userContextProvider.region}`);
        break;
    }
  }

  setupNaTableColumns() {
    this.tableColumns = [
      'reservationId',
      'reservationReferenceId',
      'fullName',
      'verifiedDate'
    ];
  }

  setupEuTableColumns() {
    this.tableColumns = [
      'reservationId',
      'reservationReferenceId',
      'fullName',
      'verifiedDate'
    ];
  }

  updateTableDataSource(guests: GuestVerificationInfo[], totalRecords: number) {
    this.dataSource = new MatTableDataSource(guests);
    this.dataSource.sortData = (data: any[], _: MatSort) => data;
    this.dataSource.sort = this.sort;
    this.tableLength = totalRecords;
  }

  onPageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.searchService.setPageSize(event.pageSize);
    this.searchService.setPage(event.pageIndex + 1);
    this.loadPage();
  }

  onSearchEvent(keyword: string) {
    this.keyword = keyword;
    this.searchService.addTextQuery(keyword);
    this.searchService.setPage(1);
    this.loadPage();
  }

  onSortEvent(event: any) {
    const sortParams = this.buildSortParams(event);
    if (sortParams.sortField === 'id') {
      sortParams.sortField = 'guestId';
    }

    this.searchService.addSort(sortParams);
    this.loadPage();
  }

  buildSortParams(event: any): SortParams {
    const { active: sortField, direction } = event;

    let sortOrder: SortOrderType;
    if (direction === 'asc') {
      sortOrder = 1;
    } else {
      sortOrder = -1;
    }

    return {
      sortField,
      sortOrder
    }
  }

  onDeleteKeyUp(searchKeyword: any) {
    if (searchKeyword === '') {
      this.onSearchEvent('');
    }
  }

  clearSearch(searchKeyword: any) {
    searchKeyword.value = '';
    this.onSearchEvent('');
  }

  onFilterPillCloseEvent(filterId: string) {
    this.filterMessageService.onClearClicked(filterId);
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
  }

  onGoToPageClick() {
    if (!this.pageNumber) {
      return;
    }

    const max = Math.trunc(this.paginator.length / this.paginator.pageSize);
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;

      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.snackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  onRowClick(row: any): void {
    this.router.navigate([`/equifax-lookup/detail/${row['id']}`]);
  }

  onNewGuestClick() {
    this.router.navigate(['/verify-guest']);
  }

  onEquifaxResultsClick() {
    this.router.navigate(['/equifax-lookup']);
  }

  updateVerificationResultFilterAfterFilterApplied(statusEnabled: boolean, statusName: string) {
    if (!this.guestFilterStateService.isPassedFilterActive() && !this.guestFilterStateService.isConsiderFilterActive() && !this.guestFilterStateService.isProcessingFilterActive() && !this.guestFilterStateService.isNoVerificationFilterActive()) {
      this.returnEmptyDataSource = true;
    } else {
      this.returnEmptyDataSource = false;
    }

    if (statusEnabled) {
      this.updateVerificationResultFilter(statusName);
    } else {
      this.removeVerificationFilterValue(statusName);
    }
  }

  updateVerificationResultFilter(verificationResultCode: string) {
    this.searchService.updateFilter({
      fieldName: 'verificationResultCode',
      value: [verificationResultCode],
      range: false
    });
  }

  removeVerificationFilterValue(verificationResultCode: string) {
    this.searchService.removeValueFromFilter('verificationResultCode', verificationResultCode);
  }

  updateFilterPillTextAfterFilterEvent() {
    this.filterMessageService.activeEvent.subscribe((event: ActiveEventMessage) => {
      if (!this.filterDefinitions) {
        return;
      }

      this.resetPaginator(1, true);
      if (event.active) {
        let pillText = this.filterDefinitions.find(definition => definition.id == event.filterId)?.shortName || '';
        if (event.value) {
          pillText = pillText + " : " + event.value;
        }
        this.filterPills.set(event.filterId, { filterId: event.filterId, text: pillText });
      } else {
        this.filterPills.delete(event.filterId);
      }
    });
  }

  toggleLegendVisibility() {
    this.legendVisible = !this.legendVisible;
  }

  getLegendVisibilityToggleText() {
    return this.legendVisible ? 'Hide [-]' : 'Show [+]';
  }
}